import { Component } from '@theme/component';

/**
 * Gift options in the buy box: a "this is a gift" toggle, and the To / From / message fields it
 * reveals.
 *
 * The reveal itself is CSS. This component exists for the one thing CSS cannot do, which is
 * decide whether a field is submitted at all.
 *
 * A line item property is posted for every named input inside the add-to-cart form, whether or
 * not the shopper can see it. Someone who types a message, changes their mind and unticks the
 * box would otherwise still get it printed on their card. So the rule here is: a field carries
 * its `name` only while the toggle is on and the field has something in it. Taking the name off
 * rather than setting `disabled` keeps the field focusable and typable, so a draft survives being
 * unticked and re-ticked.
 *
 * Nothing is ever made required. The fields are an offer, and add-to-cart has to work with every
 * one of them left empty.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} toggle - The "this is a gift" checkbox.
 * @property {HTMLElement} fields - The wrapper the toggle reveals.
 * @property {HTMLElement} [counter] - The message's live character count.
 *
 * @extends {Component<Refs>}
 */
class PdpGiftOptions extends Component {
  requiredRefs = ['toggle', 'fields'];

  /** @type {(event: Event) => void} */
  #onBlockSelect = () => {};

  connectedCallback() {
    super.connectedCallback();

    this.#syncNames();
    this.#updateCounter();

    // Theme editor: selecting the block should show what the block does, and everything
    // interesting about it is behind the toggle.
    this.#onBlockSelect = (event) => {
      if (!(event.target instanceof Node) || !this.contains(event.target)) return;

      this.refs.toggle.checked = true;
      this.#syncNames();
    };

    document.addEventListener('shopify:block:select', this.#onBlockSelect);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    super.disconnectedCallback();
  }

  handleToggle() {
    this.#syncNames();
  }

  handleInput() {
    this.#syncNames();
    this.#updateCounter();
  }

  /** @returns {HTMLInputElement[] | HTMLTextAreaElement[]} */
  get #inputs() {
    return /** @type {any} */ ([...this.querySelectorAll('[data-property]')]);
  }

  /**
   * Puts the `name` on the fields that should post and takes it off the ones that should not.
   */
  #syncNames() {
    const active = this.refs.toggle.checked;

    for (const input of this.#inputs) {
      const property = input.dataset.property;
      if (!property) continue;

      if (active && input.value.trim() !== '') {
        input.setAttribute('name', property);
      } else {
        input.removeAttribute('name');
      }
    }
  }

  #updateCounter() {
    const { counter } = this.refs;
    if (!counter) return;

    const textarea = this.querySelector('textarea[data-property]');
    if (!(textarea instanceof HTMLTextAreaElement)) return;

    const template = counter.getAttribute('data-template');
    if (!template) return;

    counter.textContent = template
      .replace('[used]', String(textarea.value.length))
      .replace('[max]', String(textarea.maxLength));
  }
}

if (!customElements.get('pdp-gift-options-component')) {
  customElements.define('pdp-gift-options-component', PdpGiftOptions);
}
